import React from 'react';
import { motion } from 'framer-motion';

interface PortfolioItem {
  id: number;
  title: string;
  description: string;
  benefits: string[];
}

const PortfolioShowcase: React.FC = () => {
  const portfolioItems: PortfolioItem[] = [
    {
      id: 1,
      title: "Competitor Tracking",
      description: "Scrapes IG reels in your niche, extracts ideas, strategic angles, target audience, and generates how your business can use those ideas.",
      benefits: ["Stay ahead of competitors", "Get fresh content ideas", "Daily email updates"]
    },
    {
      id: 2,
      title: "Elon Musk AI Persona Bot",
      description: "Replicates your experience & personality. For coaches, this can be a downsell to leads who can't afford full coaching.",
      benefits: ["Additional revenue stream", "Scalable client interactions", "WhatsApp/Slack/Telegram integration"]
    },
    {
      id: 3,
      title: "LinkedIn Content Automation",
      description: "Converts marketing books, quotes, summaries, and lessons into LinkedIn content using Claude, with approval workflow.",
      benefits: ["Consistent posting schedule", "High-quality content", "Minimal time investment"]
    },
    {
      id: 4,
      title: "Twitter Content Automation",
      description: "Converts competitor's LinkedIn posts to content in your tone using Claude, with options for longform and tweets.",
      benefits: ["Competitive content strategy", "Multiple content formats", "Automated posting"]
    },
    {
      id: 5,
      title: "Meetings to Content Automation",
      description: "Scrapes meeting transcripts, summaries and notes, and converts them to content in your tone and voice.",
      benefits: ["Repurpose meeting content", "Consistent brand voice", "Automated workflow"]
    },
    {
      id: 6,
      title: "Invoicing Follow Up System",
      description: "Fetches Stripe Invoices, checks due dates, sends follow up emails, and removes paid invoices from the flow.",
      benefits: ["Less headaches and stress", "No time wasted in follow ups", "Increases revenue"]
    },
    {
      id: 7,
      title: "Proposal System",
      description: "Fill in a form with client info, problem, solution, etc. GPT makes your answers sound professional and fills in a proposal template.",
      benefits: ["Decreasing sales cycle", "Consistent branding", "Professional formatting"]
    },
    {
      id: 8,
      title: "Newsletter System",
      description: "Scrapes content from reddit, checks relevancy, converts them into newsletter content, and sends based on schedule.",
      benefits: ["Saves time", "Produces fresh relevant ideas", "Seamless scheduling"]
    },
    {
      id: 9,
      title: "YouTube Comments to Scripts",
      description: "Scrapes YouTube comments, identifies questions/suggestions/ideas, and converts them into rough YouTube scripts.",
      benefits: ["Never run out of content ideas", "Solves blank page syndrome", "Creates content people want"]
    },
    {
      id: 10,
      title: "Cold Email System",
      description: "Fill in links of lead searches from Sales Navigator, scrapes & finds emails, and sends emails via Instantly.",
      benefits: ["More leads, more revenue", "Reduces manual work", "High quality lead list"]
    },
    {
      id: 11,
      title: "Indeed Job Resume Customizer",
      description: "Scrapes jobs, analyzes requirements and descriptions, and fills in Google doc resume template.",
      benefits: ["Apply to jobs faster", "Higher interview chances", "No more manual resume customization"]
    },
    {
      id: 12,
      title: "Website Customer Support Bot",
      description: "Collects lead info, answers questions related to product/service, and books meetings.",
      benefits: ["Captures leads with less friction", "Works 24/7", "Increases revenue"]
    }
  ];

  return (
    <div className="gradient-bg py-24">
      <div className="container mx-auto px-4">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold text-center mb-4"
        >
          Portfolio Showcase
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-16"
        >
          Explore our diverse range of AI automation solutions designed to streamline operations and drive growth.
        </motion.p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              className="bg-black/30 backdrop-blur-sm rounded-xl overflow-hidden h-full"
            >
              <div className="p-6 h-full flex flex-col">
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-300 mb-6 flex-grow">{item.description}</p>
                
                <div className="mt-auto">
                  <h4 className="text-accent font-medium mb-2">Benefits:</h4>
                  <ul className="space-y-1">
                    {item.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-300 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioShowcase;
